<script setup lang="ts">
/**
 * 「我的应用」页 /me（官网单点登录，设计文档 §5.3）。
 *
 * 进入页面先 GET /api/v1/me：
 *   · 已登录 → 账号卡片 + 已安装应用网格（分页「加载更多」）；
 *   · 401   → 登录引导卡片。**不自动跳转登录**：回调失败或会话被撤销时自动跳转会形成死循环；
 *   · 开关关闭（404 / 功能未开启）→ 说明暂未开放，给回首页与下载蜜罐；
 *   · 网络错误 / 超时 / 5xx → 暂时性故障先由 account.ts 自动退避重试（期间保持骨架屏），
 *     仍失败才给错误卡：「重新加载」「重新登录」（returnTo=/me），「返回首页」降为文字链接。
 *
 * 只读展示：不提供安装、卸载、排序（§1 不做）。uid 由后端从会话取，前端不传。
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import {
  accountState,
  buildLoginUrl,
  fetchInstalledApps,
  formatAppTime,
  InstalledAppsPageSize,
  loadAccount,
  markSignedOut,
  MyAppsPath,
  readCachedFirstPage,
  writeCachedFirstPage,
  type InstalledApp,
  type InstalledAppsResult,
} from './account'
import { buildPublicApiUrl } from './official-promotion'
import { canonicalizeInviteCode, hasStandardInviteFormat, isCustomInviteCode } from './promotion-config'
import brandMark from './assets/brand-mark.png'
import AccountEntry from './components/AccountEntry.vue'
import { Icon } from './components/Icon'
import { enhancePage } from './page-enhance'

document.title = '我的应用 - 蜜罐'

/**
 * 下载蜜罐复用站内同一份 SDK（index.html 以 script 标签引入）。本页没有官网首页那套自绘弹窗，
 * 所以按「零配置接入方」的默认配置使用：微信 / QQ 引导遮罩、iOS 提示、下载进度层都由 SDK 自带。
 * SDK 没加载成功（极端网络）时退回直连下载接口，按钮不至于点了没反应。
 */
const HoneypotLaunch = typeof window !== 'undefined' ? window.HoneypotLaunch : undefined
if (HoneypotLaunch) HoneypotLaunch.setup()

const loginUrl = buildLoginUrl(MyAppsPath)

/* ── 应用列表 ──────────────────────────────────────────────────────────── */

type ListStatus = 'idle' | 'loading' | 'ready' | 'error'

const apps = ref<InstalledApp[]>([])
const total = ref(0)
const page = ref(0)
const listStatus = ref<ListStatus>('idle')
const moreStatus = ref<'idle' | 'loading' | 'error'>('idle')
/** 后端返回空页但 total 仍更大（数据在翻页间被删）时停止翻页，避免「加载更多」永远点不完。 */
const exhausted = ref(false)
/** 图标加载失败的条目（按下标记录）。 */
const brokenIcons = ref<Record<number, true>>({})
const listElement = ref<HTMLElement | null>(null)

const hasMore = computed(() => listStatus.value === 'ready' && !exhausted.value && apps.value.length < total.value)
const nowMs = ref(Date.now())

/** 骨架屏的卡片数：首屏按一页的前 6 个画，足够铺满桌面三列两行。 */
const skeletonCards = [0, 1, 2, 3, 4, 5]

function applyFirstPage(result: Extract<InstalledAppsResult, { kind: 'ok' }>): void {
  nowMs.value = Date.now()
  apps.value = result.list
  total.value = Math.max(result.total, result.list.length)
  page.value = 1
  exhausted.value = result.list.length === 0
  listStatus.value = 'ready'
}

/**
 * 拉第一页。60s 内有同一账号的缓存时先渲染缓存（不出骨架屏），再后台刷新；
 * 后台刷新暂时失败时保留缓存内容，不打成错误页。
 */
async function loadFirstPage(): Promise<void> {
  const uid = signedInUid.value
  if (!uid) return
  moreStatus.value = 'idle'
  exhausted.value = false
  brokenIcons.value = {}
  const cached = readCachedFirstPage(uid, InstalledAppsPageSize)
  if (cached) applyFirstPage(cached)
  else listStatus.value = 'loading'
  const result = await fetchInstalledApps(1, InstalledAppsPageSize)
  if (signedInUid.value !== uid) return // 期间换了账号 / 已退出：结果作废
  if (result.kind === 'unauthorized') {
    markSignedOut()
    listStatus.value = 'idle'
    return
  }
  if (result.kind === 'error') {
    if (!cached) listStatus.value = 'error'
    return
  }
  writeCachedFirstPage(uid, result, InstalledAppsPageSize)
  // 用户已经在缓存上翻到后面几页时不覆盖（避免把已加载的内容收回去）
  if (cached && page.value > 1) return
  applyFirstPage(result)
}

async function loadMore(): Promise<void> {
  if (moreStatus.value === 'loading' || !hasMore.value) return
  moreStatus.value = 'loading'
  const nextPage = page.value + 1
  const firstNewIndex = apps.value.length
  const result = await fetchInstalledApps(nextPage, InstalledAppsPageSize)
  if (result.kind === 'unauthorized') {
    markSignedOut()
    moreStatus.value = 'idle'
    return
  }
  if (result.kind === 'error') {
    moreStatus.value = 'error'
    return
  }
  nowMs.value = Date.now()
  apps.value = apps.value.concat(result.list)
  total.value = Math.max(result.total, apps.value.length)
  page.value = nextPage
  if (result.list.length === 0) exhausted.value = true
  moreStatus.value = 'idle'
  // 键盘 / 读屏用户：焦点移到新加载的第一项，不用从头再 Tab 一遍（搜索筛选中时下标对不上，不挪焦点）
  if (result.list.length && !query.value.trim()) {
    await nextTick()
    const card = listElement.value?.querySelectorAll<HTMLElement>('.official-me-app')[firstNewIndex]
    card?.focus()
  }
}

/** 当前已登录账号的 uid（未登录 / 加载中 / 失败时为空）。 */
const signedInUid = computed(() => (accountState.value.kind === 'signed-in' ? accountState.value.user.uid : ''))

// 登录态确定为「已登录」后才拉列表；账号变化（缓存态被后台刷新纠正为另一个账号）时重拉；
// 会话在使用中失效（列表 401）时自动切到登录引导。
watch(signedInUid, uid => {
  apps.value = []
  total.value = 0
  page.value = 0
  listStatus.value = 'idle'
  if (uid) void loadFirstPage()
}, { immediate: true })

function retryAccount(): void {
  void loadAccount(true)
}

/* ── 展示辅助 ──────────────────────────────────────────────────────────── */

const user = computed(() => (accountState.value.kind === 'signed-in' ? accountState.value.user : null))

/** 图标加载失败 / 没有图标时的占位：取应用名首字，底色按名字在三种低饱和色里固定挑一个。 */
const FallbackTones = ['a', 'b', 'c']
function fallbackTone(name: string): string {
  let sum = 0
  for (let i = 0; i < name.length; i++) sum = (sum + name.charCodeAt(i)) % 997
  return FallbackTones[sum % FallbackTones.length]
}

/** 每一行要展示的时间（相对 + 绝对），随列表与「现在」一起重算。 */
const appRows = computed(() => apps.value.map((app, index) => ({
  app,
  index,
  installed: formatAppTime(app.installTime, nowMs.value),
  updated: formatAppTime(app.updateTime, nowMs.value),
})))

/* ── 列表工具条：搜索（只筛已加载的条目）/ 列表 · 卡片视图 ──────────────────── */

/** 视图只记在内存里：本站约定前端不写 localStorage / sessionStorage。 */
const view = ref<'list' | 'cards'>('list')
const query = ref('')
const normalizedQuery = computed(() => query.value.trim().toLowerCase())

const visibleRows = computed(() => {
  const q = normalizedQuery.value
  if (!q) return appRows.value
  return appRows.value.filter(row => row.app.appName.toLowerCase().indexOf(q) !== -1)
})

/** 顶部统计卡「最近安装」：已加载条目里安装时间最新的一个。 */
const latestInstall = computed(() => {
  let best: InstalledApp | null = null
  for (const app of apps.value) {
    if (app.installTime && (!best || app.installTime > best.installTime)) best = app
  }
  return best ? { name: best.appName, time: formatAppTime(best.installTime, nowMs.value) } : null
})

/* ── 「用邀请码安装」：展开一个输入框，提交后走官网首页的邀请流程 ─────────────── */

const invitePanelOpen = ref(false)

async function openInvitePanel(): Promise<void> {
  invitePanelOpen.value = true
  await nextTick()
  const input = document.getElementById('me-invite-input') as HTMLInputElement | null
  input?.focus()
}

/* ── 空状态：下载蜜罐 / 输入邀请码（复用站内既有能力）────────────────────── */

const downloadHint = ref('')

function handleDownload(): void {
  downloadHint.value = ''
  if (!HoneypotLaunch) {
    window.location.assign(buildPublicApiUrl('/public/pkg/download'))
    return
  }
  // restricted / unsupported-ios 由 SDK 自带的引导层说明，这里只兜「配置错误」这种静默失败
  void HoneypotLaunch.download().then(result => {
    if (result === 'config-error') downloadHint.value = '下载暂时不可用，请返回官网首页下载。'
  })
}

const inviteInput = ref('')
const inviteError = ref('')

/**
 * 输入邀请码后交给官网首页的邀请流程（/?code=），与打开好友分享链接走完全相同的解析、
 * 校验位检查与拉起逻辑。这里只做最基本的格式提示，是否可用仍由后端裁决。
 */
function openInvite(): void {
  const raw = inviteInput.value.trim()
  if (!raw) {
    inviteError.value = '请输入邀请码'
    return
  }
  const code = canonicalizeInviteCode(raw)
  if (!code || (!hasStandardInviteFormat(code) && !isCustomInviteCode(code))) {
    inviteError.value = '邀请码格式不正确，请检查后重试'
    return
  }
  inviteError.value = ''
  window.location.assign(`/?code=${encodeURIComponent(code)}`)
}

onMounted(() => {
  void loadAccount()
  void nextTick(enhancePage)
})
</script>

<template>
  <a class="official-skip-link" href="#me-main">跳到主要内容</a>
  <header class="official-header official-me-header">
    <div class="official-shell official-shell-wide official-header-inner">
      <div class="official-header-main">
        <a class="official-brand official-me-brand" href="/" aria-label="返回蜜罐首页">
          <img :src="brandMark" alt="蜜罐" width="24" height="28" />
          <span>蜜罐</span>
        </a>
        <nav class="official-nav official-me-nav" aria-label="页面导航">
          <a href="/">首页</a>
          <a href="/me" aria-current="page">我的应用</a>
        </nav>
      </div>
      <div class="official-header-actions">
        <AccountEntry current="me" />
      </div>
    </div>
  </header>

  <main id="me-main" class="official-me" tabindex="-1">
    <div class="official-shell official-shell-wide">
      <nav class="official-me-crumbs" aria-label="当前位置">
        <a href="/">首页</a>
        <span aria-hidden="true">/</span>
        <span aria-current="page">我的应用</span>
      </nav>

      <div class="official-me-titlebar">
        <div class="official-me-titlebar-copy">
          <h1>我的应用</h1>
          <p class="official-me-lead">与蜜罐 App 中的「我的应用」保持同步。安装与管理请在 App 中进行。</p>
        </div>
        <button v-if="user" id="me-invite-open" class="official-me-invite-open" type="button" :aria-expanded="invitePanelOpen ? 'true' : 'false'" @click="openInvitePanel">
          <Icon icon="lucide:plus" aria-hidden="true" />
          用邀请码安装
        </button>
      </div>

      <!-- 「用邀请码安装」展开的输入框（空状态下页面里已有同一个表单，不再重复） -->
      <form v-if="user && invitePanelOpen && !(listStatus === 'ready' && apps.length === 0)" class="official-me-card official-me-invite-panel official-me-invite-form" novalidate @submit.prevent="openInvite">
        <label for="me-invite-input"><strong>有邀请码？</strong><span>输入好友分享的邀请码，打开对应的应用。</span></label>
        <div class="official-me-invite-field">
          <input
            id="me-invite-input"
            v-model="inviteInput"
            type="text"
            inputmode="text"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            maxlength="40"
            placeholder="13 位邀请码或自定义邀请码"
            :aria-invalid="inviteError ? 'true' : 'false'"
            :aria-describedby="inviteError ? 'me-invite-error' : undefined"
            @input="inviteError = ''"
          />
          <button id="me-invite-submit" class="official-button official-button-primary" type="submit">打开</button>
        </div>
        <p v-if="inviteError" id="me-invite-error" class="official-me-field-error" role="alert">{{ inviteError }}</p>
      </form>

      <!-- 概览两张卡：已安装数 / 最近安装（账号信息只在右上角入口里，不再重复） -->
      <div v-if="user" class="official-me-stats">
        <div class="official-me-card official-me-stat">
          <span class="official-me-stat-label">已安装应用</span>
          <strong class="official-me-stat-value">{{ listStatus === 'ready' ? total : '—' }}</strong>
        </div>
        <div class="official-me-card official-me-stat">
          <span class="official-me-stat-label">最近安装</span>
          <strong class="official-me-stat-name">{{ latestInstall ? latestInstall.name : '—' }}</strong>
          <span v-if="latestInstall && latestInstall.time" class="official-me-stat-sub">{{ latestInstall.time.text }}</span>
        </div>
      </div>
      <div v-else-if="accountState.kind === 'loading'" class="official-me-stats" aria-hidden="true">
        <div v-for="n in 2" :key="n" class="official-me-card official-me-stat">
          <span class="official-me-skel official-me-skel-line official-me-skel-w40"></span>
          <span class="official-me-skel official-me-skel-line official-me-skel-w60"></span>
        </div>
      </div>

      <section class="official-me-body" aria-labelledby="me-apps-title">
        <!-- 加载中：骨架屏 -->
        <div v-if="accountState.kind === 'loading' || (user && listStatus !== 'ready' && listStatus !== 'error')" id="me-skeleton" class="official-me-card official-me-loading" role="status" aria-live="polite">
          <span class="official-me-visually-hidden">正在加载你的应用…</span>
          <div class="official-me-toolbar" aria-hidden="true">
            <span class="official-me-skel official-me-skel-title"></span>
          </div>
          <ul class="official-me-list" aria-hidden="true">
            <li v-for="n in skeletonCards" :key="n" class="official-me-app official-me-app-skeleton">
              <div class="official-me-app-main">
                <span class="official-me-skel official-me-skel-icon"></span>
                <div class="official-me-app-title">
                  <span class="official-me-skel official-me-skel-line official-me-skel-w70"></span>
                </div>
              </div>
              <div class="official-me-app-cell"><span class="official-me-skel official-me-skel-line official-me-skel-w60"></span></div>
              <div class="official-me-app-cell"><span class="official-me-skel official-me-skel-line official-me-skel-w80"></span></div>
            </li>
          </ul>
          <h2 id="me-apps-title" class="official-me-visually-hidden">已安装的应用</h2>
        </div>

        <!-- 未登录：登录引导（不自动跳转，避免循环） -->
        <div v-else-if="accountState.kind === 'anonymous'" id="me-login-guide" class="official-me-card official-me-panel official-me-guide">
          <span class="official-me-panel-icon" aria-hidden="true"><Icon icon="lucide:lock" /></span>
          <h2 id="me-apps-title">登录后查看你的应用</h2>
          <p>使用蜜罐 App 的同一个聚点账号登录，即可在网页上查看已安装的应用。</p>
          <ul class="official-me-guide-points">
            <li><Icon icon="lucide:check" aria-hidden="true" /><span>支持邮箱验证码登录，新邮箱自动注册</span></li>
            <li><Icon icon="lucide:check" aria-hidden="true" /><span>在聚点账号页面输入，官网不接触你的密码</span></li>
            <li><Icon icon="lucide:check" aria-hidden="true" /><span>网页登录不影响手机上蜜罐 App 的登录状态</span></li>
          </ul>
          <div class="official-me-panel-actions">
            <a id="me-login" class="official-button official-button-primary" :href="loginUrl"><Icon icon="lucide:log-in" aria-hidden="true" /> 登录聚点账号</a>
            <a class="official-button official-button-secondary" href="/"><Icon icon="lucide:house" aria-hidden="true" /> 返回首页</a>
          </div>
        </div>

        <!-- 开关关闭：暂未开放 -->
        <div v-else-if="accountState.kind === 'disabled'" id="me-disabled" class="official-me-card official-me-panel">
          <span class="official-me-panel-icon" aria-hidden="true"><Icon icon="lucide:info" /></span>
          <h2 id="me-apps-title">网页版「我的应用」暂未开放</h2>
          <p>你可以在蜜罐 App 中查看已安装的应用。还没有蜜罐的话，可以先下载 Android 版。</p>
          <div class="official-me-panel-actions">
            <button class="official-button official-button-primary" type="button" @click="handleDownload"><Icon icon="lucide:download" aria-hidden="true" /> 下载蜜罐</button>
            <a class="official-button official-button-secondary" href="/"><Icon icon="lucide:house" aria-hidden="true" /> 返回首页</a>
          </div>
        </div>

        <!-- 账号信息拿不到（自动重试后仍失败）：重新加载 / 重新登录 -->
        <div v-else-if="accountState.kind === 'error'" id="me-account-error" class="official-me-card official-me-panel official-me-panel-error" role="alert">
          <span class="official-me-panel-icon" aria-hidden="true"><Icon icon="lucide:circle-alert" /></span>
          <h2 id="me-apps-title">暂时无法获取账号信息</h2>
          <p>可能是网络不稳定或服务繁忙，请稍后重试。</p>
          <div class="official-me-panel-actions">
            <button id="me-account-retry" class="official-button official-button-primary" type="button" @click="retryAccount"><Icon icon="lucide:refresh-cw" aria-hidden="true" /> 重新加载</button>
            <a id="me-account-relogin" class="official-button official-button-secondary" :href="loginUrl"><Icon icon="lucide:log-in" aria-hidden="true" /> 重新登录</a>
          </div>
          <a id="me-account-home" class="official-me-panel-link" href="/">返回首页</a>
        </div>

        <!-- 已登录：列表加载失败 -->
        <div v-else-if="listStatus === 'error'" id="me-apps-error" class="official-me-card official-me-panel official-me-panel-error" role="alert">
          <span class="official-me-panel-icon" aria-hidden="true"><Icon icon="lucide:circle-alert" /></span>
          <h2 id="me-apps-title">应用列表没有加载出来</h2>
          <p>可能是网络不稳定或服务繁忙，请稍后重试。</p>
          <div class="official-me-panel-actions">
            <button id="me-apps-retry" class="official-button official-button-primary" type="button" @click="loadFirstPage"><Icon icon="lucide:refresh-cw" aria-hidden="true" /> 重新加载</button>
          </div>
        </div>

        <!-- 已登录：空状态 -->
        <div v-else-if="apps.length === 0" id="me-empty" class="official-me-card official-me-panel official-me-empty">
          <span class="official-me-panel-icon" aria-hidden="true"><Icon icon="lucide:layout-grid" /></span>
          <h2 id="me-apps-title">还没有安装应用</h2>
          <p>在蜜罐中输入好友分享的邀请码即可安装应用，安装后会出现在这里。</p>
          <div class="official-me-empty-options">
            <div class="official-me-empty-option">
              <strong>还没有蜜罐？</strong>
              <span>先下载蜜罐 Android 版，安装后用同一账号登录。</span>
              <button id="me-empty-download" class="official-button official-button-primary" type="button" @click="handleDownload"><Icon icon="lucide:download" aria-hidden="true" /> 下载蜜罐</button>
              <p v-if="downloadHint" class="official-me-field-error" role="alert">{{ downloadHint }}</p>
            </div>
            <form class="official-me-empty-option official-me-invite-form" novalidate @submit.prevent="openInvite">
              <label for="me-invite-input"><strong>有邀请码？</strong><span>输入好友分享的邀请码，打开对应的应用。</span></label>
              <div class="official-me-invite-field">
                <input
                  id="me-invite-input"
                  v-model="inviteInput"
                  type="text"
                  inputmode="text"
                  autocomplete="off"
                  autocapitalize="characters"
                  spellcheck="false"
                  maxlength="40"
                  placeholder="13 位邀请码或自定义邀请码"
                  :aria-invalid="inviteError ? 'true' : 'false'"
                  :aria-describedby="inviteError ? 'me-invite-error' : undefined"
                  @input="inviteError = ''"
                />
                <button id="me-invite-submit" class="official-button official-button-secondary" type="submit"><Icon icon="lucide:key-round" aria-hidden="true" /> 打开</button>
              </div>
              <p v-if="inviteError" id="me-invite-error" class="official-me-field-error" role="alert">{{ inviteError }}</p>
            </form>
          </div>
        </div>

        <!-- 已登录：应用列表（默认列表视图，可切卡片视图） -->
        <div v-else id="me-apps" class="official-me-card official-me-apps" :class="`official-me-view-${view}`">
          <div class="official-me-toolbar">
            <div class="official-me-toolbar-title">
              <h2 id="me-apps-title">全部应用</h2>
              <span id="me-apps-total" class="official-me-count">共 {{ total }} 个</span>
            </div>
            <div class="official-me-toolbar-tools">
              <label class="official-me-search">
                <Icon icon="lucide:search" aria-hidden="true" />
                <span class="official-me-visually-hidden">搜索已加载的应用</span>
                <input v-model="query" type="search" placeholder="搜索应用名称" autocomplete="off" />
              </label>
              <div class="official-me-views" role="group" aria-label="视图">
                <button id="me-view-list" type="button" :aria-pressed="view === 'list' ? 'true' : 'false'" aria-label="列表视图" @click="view = 'list'"><Icon icon="lucide:list" aria-hidden="true" /></button>
                <button id="me-view-cards" type="button" :aria-pressed="view === 'cards' ? 'true' : 'false'" aria-label="卡片视图" @click="view = 'cards'"><Icon icon="lucide:layout-grid" aria-hidden="true" /></button>
              </div>
            </div>
          </div>

          <div class="official-me-list-head" aria-hidden="true">
            <span>应用</span><span>版本</span><span>安装时间</span><span>最近更新</span><span>状态</span>
          </div>
          <ul ref="listElement" class="official-me-list">
            <li v-for="{ app, index, installed, updated } in visibleRows" :key="`${index}-${app.appName}`" class="official-me-app" tabindex="-1">
              <div class="official-me-app-main">
                <span class="official-me-app-icon">
                  <img v-if="app.icon && !brokenIcons[index]" :src="app.icon" alt="" width="36" height="36" loading="lazy" decoding="async" referrerpolicy="no-referrer" @error="brokenIcons[index] = true" />
                  <span v-else class="official-me-app-fallback" :class="`official-me-app-fallback-${fallbackTone(app.appName)}`" data-icon-fallback aria-hidden="true">{{ app.appName.charAt(0) }}</span>
                </span>
                <div class="official-me-app-title">
                  <h3 :title="app.appName">{{ app.appName }}</h3>
                </div>
              </div>
              <div class="official-me-app-cell official-me-app-version"><span class="official-me-cell-label">版本</span>{{ app.versionName || '未知' }}</div>
              <div class="official-me-app-cell">
                <span class="official-me-cell-label">安装时间</span><time v-if="installed" :datetime="installed.iso" :title="installed.full" data-time="install">{{ installed.text }}</time><span v-else>—</span>
              </div>
              <div class="official-me-app-cell">
                <span class="official-me-cell-label">最近更新</span><time v-if="updated" :datetime="updated.iso" :title="updated.full" data-time="update">{{ updated.text }}</time><span v-else>—</span>
              </div>
              <div class="official-me-app-cell official-me-app-status"><span class="official-me-status"><i aria-hidden="true"></i>已安装</span></div>
            </li>
          </ul>
          <p v-if="normalizedQuery && visibleRows.length === 0" class="official-me-no-match">已加载的应用里没有「{{ query.trim() }}」，可以加载更多后再试。</p>

          <div class="official-me-more">
            <p id="me-apps-progress" class="official-me-progress" aria-live="polite">已显示 {{ apps.length }} / {{ total }} 个应用</p>
            <div class="official-me-more-actions">
              <p v-if="moreStatus === 'error'" id="me-more-error" class="official-me-more-error" role="alert">
                <Icon icon="lucide:circle-alert" aria-hidden="true" /><span>加载失败，请点击「加载更多」重试。</span>
              </p>
              <button
                v-if="hasMore"
                id="me-load-more"
                class="official-me-page-button"
                type="button"
                :disabled="moreStatus === 'loading'"
                :aria-busy="moreStatus === 'loading' ? 'true' : 'false'"
                @click="loadMore"
              >
                <Icon v-if="moreStatus === 'loading'" class="official-me-spin" icon="lucide:loader-circle" aria-hidden="true" />
                {{ moreStatus === 'loading' ? '正在加载…' : '加载更多' }}
              </button>
              <p v-else-if="apps.length > InstalledAppsPageSize" class="official-me-done">已全部加载</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>

  <footer class="official-footer official-me-footer">
    <div class="official-shell official-shell-wide official-footer-bottom">
      <p>© 2026 蜜罐</p>
      <p>仅提供 Android 版本</p>
    </div>
  </footer>
</template>
